// src/components/MaintenanceLists/MaintenanceModal.js
import React from 'react';
import { Form, Button } from 'react-bootstrap';
import Modal from '../Modal';
import { MAINTENANCE_CATEGORIES, MAINTENANCE_SUBCATEGORIES } from './constants';

/**
 * Modal component for adding/editing maintenance records
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {string|null} props.editingItemId - ID of item being edited, or null if adding new
 * @param {Object} props.maintenanceItem - Maintenance item data
 * @param {Function} props.setMaintenanceItem - Function to update maintenance item data
 * @param {Function} props.addMaintenanceItem - Function to add a new maintenance item
 * @param {Function} props.editMaintenanceItem - Function to edit an existing maintenance item
 * @returns {JSX.Element} Rendered component
 */
const MaintenanceModal = ({
  isOpen,
  onClose,
  editingItemId,
  maintenanceItem,
  setMaintenanceItem,
  addMaintenanceItem,
  editMaintenanceItem
}) => {
  const isEditing = !!editingItemId;
  const modalTitle = isEditing ? "Edit Maintenance Record" : "Add Maintenance Record";
  const submitLabel = isEditing ? "Update Record" : "Add Record";
  
  // Reset form function
  const resetForm = () => {
    setMaintenanceItem({
      date: new Date().toISOString().split('T')[0],
      odometer: '',
      category: 'regular',
      subcategory: 'service',
      description: '',
      cost: '',
      location: '',
      notes: '',
      isDIY: false
    });
  };
  
  const handleClose = () => {
    onClose();
    resetForm();
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
    >
      <div className="maintenance-modal">
        <Form onSubmit={(e) => {
          e.preventDefault();
          isEditing ? editMaintenanceItem() : addMaintenanceItem();
        }}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="date">Date *</Form.Label>
            <Form.Control
              id="date"
              type="date"
              value={maintenanceItem.date}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, date: e.target.value})}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="odometer">Odometer (miles/km)</Form.Label>
            <Form.Control
              id="odometer"
              type="number"
              min="0"
              placeholder="Current odometer reading"
              value={maintenanceItem.odometer}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, odometer: e.target.value})}
            />
          </Form.Group>
  
          <Form.Group className="mb-3">
            <Form.Label htmlFor="category">Category *</Form.Label>
            <Form.Select
              id="category"
              value={maintenanceItem.category}
              onChange={(e) => setMaintenanceItem({
                ...maintenanceItem, 
                category: e.target.value,
                subcategory: MAINTENANCE_SUBCATEGORIES[e.target.value][0].id // Select first subcategory
              })}
              required
            >
              {Object.entries(MAINTENANCE_CATEGORIES).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label htmlFor="subcategory">Subcategory *</Form.Label>
            <Form.Select
              id="subcategory"
              value={maintenanceItem.subcategory}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, subcategory: e.target.value})}
              required
            >
              {MAINTENANCE_SUBCATEGORIES[maintenanceItem.category].map((subcat) => (
                <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
  
          <Form.Group className="mb-3">
            <Form.Label htmlFor="description">Description *</Form.Label>
            <Form.Control
              id="description"
              type="text"
              placeholder="Brief description of the maintenance"
              value={maintenanceItem.description}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, description: e.target.value})}
              required
            />
          </Form.Group>
  
          <Form.Group className="mb-3">
            <Form.Label htmlFor="cost">Cost (£)</Form.Label>
            <Form.Control
              id="cost"
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount spent"
              value={maintenanceItem.cost}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, cost: e.target.value})}
            />
          </Form.Group>
  
          <Form.Group className="mb-3">
            <Form.Label htmlFor="location">Location/Shop</Form.Label>
            <Form.Control
              id="location"
              type="text"
              placeholder="Where the work was done/parts purchased"
              value={maintenanceItem.location}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, location: e.target.value})}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Check
              id="isDIY"
              type="checkbox"
              label="DIY (Did the work yourself)"
              checked={maintenanceItem.isDIY}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, isDIY: e.target.checked})}
            />
          </Form.Group>
  
          <Form.Group className="mb-3">
            <Form.Label htmlFor="notes">Notes</Form.Label>
            <Form.Control
              as="textarea"
              id="notes"
              rows="3"
              placeholder="Additional details or notes"
              value={maintenanceItem.notes}
              onChange={(e) => setMaintenanceItem({...maintenanceItem, notes: e.target.value})}
            />
          </Form.Group>
  
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="success" 
              type="submit"
              disabled={!maintenanceItem.date || !maintenanceItem.description || !maintenanceItem.subcategory}
            >
              {submitLabel}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default MaintenanceModal;